/**
 * Given each item's natural width and the space available, returns how many
 * items (from the start) fit on one row. Pure and DOM-free so it's directly
 * unit-testable: same inputs always produce the same split. Always keeps at
 * least one item visible so the row isn't just a bare "More" trigger.
 */
export function computeOverflowSplit(
    itemWidths: number[],
    containerWidth: number,
    triggerWidth: number,
    gap: number,
): number {
    if (itemWidths.length === 0) {
        return 0;
    }
    const totalWidth =
        itemWidths.reduce((sum, width) => sum + width, 0) + gap * (itemWidths.length - 1);
    if (totalWidth <= containerWidth) {
        return itemWidths.length;
    }

    const budget = containerWidth - triggerWidth - gap;
    let used = 0;
    let count = 0;
    for (let i = 0; i < itemWidths.length; i++) {
        const next = used + (i === 0 ? 0 : gap) + itemWidths[i];
        if (next > budget) {
            break;
        }
        used = next;
        count = i + 1;
    }
    return Math.max(count, 1);
}

export interface OverflowSplit<T> {
    visible: T[];
    overflow: T[];
}

/**
 * Applies the natural width-based split, then guarantees `activeIndex` stays
 * visible — by swapping it in for the last naturally-visible item, not by
 * expanding the visible count. Expanding would mean "the active tab is late
 * in the list" defeats collapsing entirely, since every earlier item would
 * have to stay visible too just to keep array order. The active tab keeps
 * its relative position (last) among the visible set either way.
 */
export function applyActiveTabPin<T>(
    items: T[],
    splitCount: number,
    activeIndex: number,
): OverflowSplit<T> {
    if (activeIndex < 0 || activeIndex < splitCount) {
        return {
            visible: items.slice(0, splitCount),
            overflow: items.slice(splitCount),
        };
    }
    const prefixCount = Math.max(splitCount - 1, 0);
    const visible: T[] = [];
    const overflow: T[] = [];
    items.forEach((item, index) => {
        if (index < prefixCount || index === activeIndex) {
            visible.push(item);
        } else {
            overflow.push(item);
        }
    });
    return { visible, overflow };
}
